import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { hasPermission } from "@/lib/permissions";
import { PlaybookRunCard } from "@/components/PlaybookRunCard";
import { toast } from "sonner";
import { Workflow, Plus } from "lucide-react";

export const RiskPlaybooks = ({ riskId, user }) => {
  const canAssign = hasPermission(user, "playbook.assign");
  const [playbooks, setPlaybooks] = useState([]);
  const [runs, setRuns] = useState([]);
  const [proposals, setProposals] = useState(null);

  const pbMap = Object.fromEntries(playbooks.map((p) => [p.playbook_id, p]));

  const loadRuns = useCallback(() => {
    return api
      .get(`/playbook-runs?risk_id=${riskId}`)
      .then(({ data }) => setRuns(data))
      .catch(() => {});
  }, [riskId]);

  useEffect(() => {
    api.get("/playbooks").then(({ data }) => setPlaybooks(data)).catch(() => {});
    loadRuns();
  }, [riskId, loadRuns]);

  const propose = () => {
    api
      .post(`/risks/${riskId}/propose-playbook`)
      .then(({ data }) => {
        setProposals(data);
        if (data.length === 0) toast.info("No playbooks match this risk's theme/threshold");
      })
      .catch(() => toast.error("Failed to propose playbooks"));
  };

  const assign = async (pb) => {
    try {
      await api.post("/playbook-runs", {
        playbook_id: pb.playbook_id,
        risk_id: riskId,
        assigned_to_user_id: user.user_id,
      });
      toast.success(`Assigned “${pb.title}”`);
      setProposals(null);
      loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign playbook");
    }
  };

  const patchStep = async (runId, stepNo, state) => {
    try {
      await api.patch(`/playbook-runs/${runId}/step/${stepNo}`, { state });
      loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update step");
    }
  };

  const abort = async (runId) => {
    try {
      await api.post(`/playbook-runs/${runId}/abort`);
      toast.success("Run aborted");
      loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to abort run");
    }
  };

  const requestRemediation = async (runId, stepNo) => {
    try {
      await api.post("/remediation-actions", { run_id: runId, step_no: stepNo });
      toast.success("Remediation requested — awaiting approval");
      loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to request remediation");
    }
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between">
        <h3 className="ek-eyebrow flex items-center gap-1">
          <Workflow size={12} /> Playbooks
        </h3>
        <button
          onClick={propose}
          data-testid="propose-playbook-button"
          className="flex items-center gap-1 rounded-full border border-ek-purple/30 px-3 py-1 text-xs font-semibold text-ek-purple transition-colors hover:bg-ek-purple/5"
        >
          <Plus size={12} /> Propose playbook
        </button>
      </div>

      {/* Proposals */}
      {proposals && proposals.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="playbook-proposals">
          {proposals.map((pb) => (
            <div
              key={pb.playbook_id}
              data-testid="proposal-row"
              className="flex items-center justify-between gap-3 rounded-xl border border-ek-purple/15 bg-ek-purple/5 p-3"
            >
              <div>
                <p className="text-sm font-medium text-ek-ink">{pb.title}</p>
                <p className="text-xs text-ek-ink/65">
                  {pb.steps.length} steps · triggers ≥ {pb.trigger.min_residual_score}
                </p>
              </div>
              {canAssign ? (
                <button
                  onClick={() => assign(pb)}
                  data-testid="assign-playbook-button"
                  className="ek-btn !px-4 !py-2 text-xs"
                >
                  Assign to me
                </button>
              ) : (
                <span className="text-xs text-ek-ink/65">Assign requires playbook.assign</span>
              )}
            </div>
          ))}
        </div>
      )}
      {proposals && proposals.length === 0 && (
        <p className="mt-3 text-xs text-ek-ink/65">No matching playbooks for this risk.</p>
      )}

      {/* Runs */}
      <div className="mt-4 space-y-3">
        {runs.length === 0 ? (
          <p className="text-xs text-ek-ink/30">No playbook runs yet.</p>
        ) : (
          runs.map((run) => (
            <PlaybookRunCard
              key={run.run_id}
              run={run}
              playbook={pbMap[run.playbook_id]}
              user={user}
              onPatchStep={patchStep}
              onAbort={abort}
              onRequestRemediation={requestRemediation}
            />
          ))
        )}
      </div>
    </div>
  );
};
